import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ClsConfig} from '../../util/cls-config';
import {ClsUtil} from '../../util/cls-util';
import {firstValueFrom, Subject} from 'rxjs';
import {KeyLocalStorage} from '../models/enum/emun-enumeracion';
import {SocketService} from './socket.service';
import {retryWithNetworkAware} from "../../util/retry-with-network-aware";
import {ConexionRedService} from "./conexion-red.service";
import {TipoMensajesService} from "./tipo-mensajes.service";

// retrazo y reintentos peticion http
const delayMsLocal = 5000;

@Injectable({
  providedIn: 'root'
})
export class GenericoService extends ClsConfig {
  private http = inject(HttpClient);
  private wsService = inject(SocketService);
  private conexionRedService = inject(ConexionRedService);
  private tipoMsg = inject(TipoMensajesService);
  public maxR = 3;
  private reintentosSubject = new Subject<number>();
  reintentos$ = this.reintentosSubject.asObservable();

  constructor() {
    super();
  }

  // =========================================================
  // 🔧 Método centralizado
  // =========================================================
  private async enviarPeticion<T>(sourceFn: () => any, validarSesion = false, detectarOffline = false): Promise<T | null> {
    try {
      if (this.conexionRedService.isSlowNetwork() && !this.conexionRedService.isOnline()) {
        const mbps = this.conexionRedService.currentMbps() ?? 0;
        const rtt = this.conexionRedService.currentRtt() ?? 0;
        let mensaje: string = await ClsUtil.getTranslations(['MENSAJES.QUESTION.RED_LENTO_LATENCIA'], this.conexionRedService.infApp)['MENSAJES.QUESTION.RED_LENTO_LATENCIA'].toString();
        mensaje = mensaje.replace('{0}', mbps.toFixed(2)).replace('{1}', rtt.toFixed(0));
        const resp = await new Promise<boolean>(resolve => this.tipoMsg.mostrarQuestion((resp: boolean) => resolve(resp), mensaje)).then(value => value);
        if (!resp) { throw new Error('MENSAJES.ERROR.PETICION_CANCELADA_USER'); }
      }

      const resultado = await firstValueFrom(
        retryWithNetworkAware({
          sourceFn,
          conexionRedService: this.conexionRedService,
          maxReintentos: this.maxR,
          delayMs: delayMsLocal,
          onRetry: (retryCount: number) => {
            this.reintentosSubject.next(retryCount);
          }
        })
      );

      // detectar modo offline
      if (detectarOffline && resultado && typeof resultado === 'object') {
        if ('offline' in resultado || resultado?.body?.offline) {
          throw new Error('MENSAJES.ERROR.PETICION_CANCELADA');
        }
      }

      if (validarSesion) {
        await this.validarMensajeSesion(resultado);
      }

      return resultado as T;
    } catch (error) {
      throw new Error(error.message);
    }
  }

  // =========================================================
  // 🌐 Métodos públicos
  // =========================================================

  async enviarPeticionCloudRunLogin(data: any = {}, servicio: string, respType: string = 'text'): Promise<any> {
    const httpOptions = { responseType: respType as any };
    const sourceFn = () =>
      this.http.post(`${this.gUrlCloudRunLogin}/${servicio}`, ClsUtil.stringifyJSONBig(data), httpOptions);

    return this.enviarPeticion(sourceFn);
  }

  async enviarPeticionCloudRunSistema(data: any = {}, servicio: string, respType: string = 'text'): Promise<any> {
    const httpOptions = { responseType: respType as any };
    const sourceFn = () =>
      this.http.post(`${this.gUrlCloudRunPpto}/${servicio}`, ClsUtil.stringifyJSONBig(data), httpOptions);

    return this.enviarPeticion(sourceFn, true, true);
  }

  async enviarPeticionCloudRunNET(data: any = {}, servicio: string, respType: string = 'text'): Promise<any> {
    const httpOptions = { responseType: respType as any };
    const sourceFn = () =>
      this.http.post(`${this.gUrlCloudRunNET}/api/${servicio}`, ClsUtil.stringifyJSONBig(data), httpOptions);

    return this.enviarPeticion(sourceFn, true);
  }

  async enviarPeticionNodeJSAI(data: any = {}, servicio: string, respType: string = 'text'): Promise<any> {
    const httpOptions = { responseType: respType as any };
    const sourceFn = () =>
      this.http.post(`${this.gUrlNodeJSAI}/api/${servicio}`, ClsUtil.stringifyJSONBig(data), httpOptions);

    return this.enviarPeticion(sourceFn, false, true);
  }

  async getURLTextAwait(url: string, respType: string = 'text'): Promise<any> {
    const httpOptions = { responseType: respType as any };
    const sourceFn = () => this.http.get(url, httpOptions);

    return this.enviarPeticion(sourceFn);
  }

  // =========================================================
  // 🛡️ Validación de sesión
  // =========================================================
  private async validarMensajeSesion(resultado: any) {
    let response: any;
    try {
      response = ClsUtil.parseJSONBig(resultado);
    } catch {
      response = resultado;
    }

    let mensaje =
      response?.Errores?.ErrorDescripcion ||
      response?.ErrorDescripcion ||
      response?.errores?.errorDescripcion ||
      response?.errores?.ErrorDescripcion;

    if (mensaje && mensaje.includes('La sesión ha caducado')) {
      await this.reiniciarSistema();
    }
  }

  private async reiniciarSistema() {
    const message = ClsUtil.getTranslations(['MENSAJES.ALERT.SESION_OTROS_DIS_SISTEMA'], this.conexionRedService.infApp)['MENSAJES.ALERT.SESION_OTROS_DIS_SISTEMA'].toString();
    const reiniciar = ClsUtil.getTranslations(['MENSAJES.ALERT.REINICIAR_SISTEMA'], this.conexionRedService.infApp)['MENSAJES.ALERT.REINICIAR_SISTEMA'].toString();
    await new Promise<boolean>(resolve => this.tipoMsg.mostrarQuestion((resp: boolean) => resolve(resp), reiniciar?.toString().replace('{0}', message))).then(value => value);
    localStorage.removeItem(KeyLocalStorage.Sesion);
    localStorage.removeItem(KeyLocalStorage.ProvLogin);
    window.location.reload();
  }

  // =========================================================
  // 🔌 WebSocket
  // =========================================================
  getSocketData() {
    return this.wsService.listen('data')
  }
}
